import { useInfiniteQuery } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { fetchTimeline } from '@/approvals/api'
import type { Timeline, TimelineEvent } from '@/approvals/types'

/**
 * Лента записи страницами по курсору (#114).
 *
 * Ключ запроса — тот же `['timeline', тип, id]`, что инвалидируют формы после записи:
 * обновление перечитывает загруженные страницы, а не сбрасывает ленту к первой.
 *
 * Курсор только пересылается: он непрозрачен, и собранный на клиенте курсор — это
 * обещание о порядке, которое сервер не давал.
 */
export interface TimelineFeed {
  isLoading: boolean
  isError: boolean
  error: unknown
  /** Все загруженные страницы одним списком; `hasMore` — есть ли что дозагрузить. */
  data: { events: TimelineEvent[]; hasMore: boolean } | undefined
  /** Загружено больше одной страницы — значит, «начало» достигнуто листанием. */
  pages: number
  hasNextPage: boolean
  isFetchingNextPage: boolean
  isFetchNextPageError: boolean
  fetchNextPage: () => void
}

export function useTimeline(entityType: string, recordId: string, pageSize = 50): TimelineFeed {
  const query = useInfiniteQuery({
    queryKey: ['timeline', entityType, recordId],
    queryFn: ({ pageParam, signal }) => fetchTimeline(entityType, recordId, pageSize, signal, pageParam),
    initialPageParam: null as string | null,
    // Only a cursor the server issued moves on: `hasMore` without one would ask for the
    // first page again and show the feed twice.
    getNextPageParam: (last: Timeline) => (last.hasMore && last.nextCursor ? last.nextCursor : undefined),
    retry: false,
  })
  const pages = query.data?.pages ?? []
  return {
    isLoading: query.isLoading,
    isError: query.isError && !query.isFetchNextPageError,
    error: query.isFetchNextPageError ? null : query.error,
    data: query.data
      ? { events: pages.flatMap((page) => page.events), hasMore: query.hasNextPage }
      : undefined,
    pages: pages.length,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isFetchNextPageError: query.isFetchNextPageError,
    fetchNextPage: () => {
      void query.fetchNextPage()
    },
  }
}

/**
 * Под лентой: «Показать раньше», пока есть что листать, и «Это начало ленты» после
 * последней страницы — чтобы конец истории не читался как сломанная подгрузка.
 */
export function TimelineMore({ feed }: { feed: TimelineFeed }) {
  const { t } = useApp()
  if (feed.isFetchNextPageError) {
    return (
      <div className="row" style={{ gap: 8 }}>
        <span role="alert" className="t-sm" style={{ color: 'var(--danger-text)' }}>
          {t('timeline.moreFailed')}
        </span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={feed.fetchNextPage}>
          {t('timeline.more')}
        </button>
      </div>
    )
  }
  if (feed.hasNextPage) {
    return (
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        disabled={feed.isFetchingNextPage}
        onClick={feed.fetchNextPage}
      >
        {feed.isFetchingNextPage ? t('app.loading') : t('timeline.more')}
      </button>
    )
  }
  if (feed.pages > 1) {
    return <p className="muted t-sm">{t('timeline.beginning')}</p>
  }
  return null
}
